import { apiFetch, apiFetchStream } from '../lib/api';

export type AICopilotMessageRole = 'system' | 'user' | 'assistant' | 'tool';

export type AICopilotThreadMessage = {
  id: string;
  role: AICopilotMessageRole;
  contentText: string;
  runId?: string | null;
  createdByUserId?: string | null;
  createdAt: string;
};

export type AICopilotThreadAgent = {
  id: string;
  code: string;
  title: string;
  defaultProvider?: string | null;
  defaultModel?: string | null;
  status?: string | null;
};

export type AICopilotContextProcess = {
  id: string;
  title: string;
  status: string;
  processType: 'service' | 'internal';
  serviceId?: string | null;
  serviceCode?: string | null;
  serviceLabel?: string | null;
  offeringCode?: string | null;
  category?: string | null;
  subcategoryId?: string | null;
  subcategories: string[];
};

export type AICopilotContextMeetingNote = {
  id: string;
  eventId: string;
  orgId?: string | null;
  eventTitle?: string | null;
  meetingType?: string | null;
  eventStatus?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  summary?: string | null;
  clientUpdates?: string | null;
  decisions?: string | null;
  nextSteps?: string | null;
  internalContext?: string | null;
  createdByUserId?: string | null;
  updatedByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AICopilotThread = {
  id: string;
  title: string;
  status: string;
  ticketId?: string | null;
  orgId?: string | null;
  ownerUserId?: string | null;
  agent: AICopilotThreadAgent;
  contextProcessIds: string[];
  contextMeetingNoteIds?: string[];
  contextProcesses: AICopilotContextProcess[];
  contextMeetingNotes?: AICopilotContextMeetingNote[];
  lastActivityAt: string;
  lastMessagePreview?: string | null;
  messages: AICopilotThreadMessage[];
  createdAt: string;
  updatedAt: string;
};

export type ListTicketCopilotThreadsResponse = {
  threads: AICopilotThread[];
};

export type GetTicketCopilotThreadResponse = AICopilotThread;

export type SendTicketCopilotMessagePayload = {
  message?: string;
  threadId?: string;
  agentId?: string;
  agentCode?: string;
  processIds?: string[];
  attachments?: File[];
  bootstrapMode?: 'ticket_process_ready';
  provider?: 'openai' | 'anthropic' | 'deepseek';
  model?: string;
  temperature?: number;
  maxTokens?: number;
};

export type SendTicketCopilotMessageResponse = {
  thread: AICopilotThread;
  assistantMessage: AICopilotThreadMessage;
  run: {
    id: string;
    provider: string;
    model: string;
    status: string;
    inputTokens?: number | null;
    outputTokens?: number | null;
    finishReason?: string | null;
  };
  contextProcesses: AICopilotContextProcess[];
  contextMeetingNotes?: AICopilotContextMeetingNote[];
};

export type TicketCopilotStreamPhase =
  | 'loading_ticket_context'
  | 'loading_process_context'
  | 'reviewing_ticket_activity'
  | 'reading_attachments'
  | 'analyzing'
  | 'drafting';

export type TicketCopilotStreamEvent =
  | {
      type: 'status';
      phase: TicketCopilotStreamPhase;
      message: string;
    }
  | {
      type: 'token';
      token: string;
    }
  | {
      type: 'complete';
      response: SendTicketCopilotMessageResponse;
    }
  | {
      type: 'error';
      error: string;
    };

export function listTicketCopilotThreads(ticketId: string, options?: { agentCode?: string }) {
  const search = new URLSearchParams();
  if (options?.agentCode) {
    search.set('agentCode', options.agentCode);
  }
  const suffix = search.toString() ? `?${search.toString()}` : '';
  return apiFetch<ListTicketCopilotThreadsResponse>(
    `/api/admin/ai/tickets/${encodeURIComponent(ticketId)}/threads${suffix}`,
  );
}

export function getTicketCopilotThread(threadId: string) {
  return apiFetch<GetTicketCopilotThreadResponse>(
    `/api/admin/ai/threads/${encodeURIComponent(threadId)}`,
  );
}

export function deleteTicketCopilotThread(threadId: string) {
  return apiFetch<{ success: boolean; threadId: string }>(
    `/api/admin/ai/threads/${encodeURIComponent(threadId)}`,
    {
      method: 'DELETE',
    },
  );
}

export async function streamTicketCopilotMessage(
  ticketId: string,
  payload: SendTicketCopilotMessagePayload,
  options: {
    signal?: AbortSignal;
    onEvent: (event: TicketCopilotStreamEvent) => void;
  },
) {
  const formData = new FormData();
  if (typeof payload.message === 'string') {
    formData.append('message', payload.message);
  }
  if (typeof payload.threadId === 'string') {
    formData.append('threadId', payload.threadId);
  }
  if (typeof payload.agentId === 'string') {
    formData.append('agentId', payload.agentId);
  }
  if (typeof payload.agentCode === 'string') {
    formData.append('agentCode', payload.agentCode);
  }
  if (Array.isArray(payload.processIds) && payload.processIds.length) {
    formData.append('processIds', JSON.stringify(payload.processIds));
  }
  if (payload.bootstrapMode) {
    formData.append('bootstrapMode', payload.bootstrapMode);
  }
  if (payload.provider) {
    formData.append('provider', payload.provider);
  }
  if (typeof payload.model === 'string') {
    formData.append('model', payload.model);
  }
  if (typeof payload.temperature === 'number') {
    formData.append('temperature', String(payload.temperature));
  }
  if (typeof payload.maxTokens === 'number') {
    formData.append('maxTokens', String(payload.maxTokens));
  }
  for (const attachment of payload.attachments ?? []) {
    formData.append('attachments', attachment);
  }

  const response = await apiFetchStream(
    `/api/admin/ai/tickets/${encodeURIComponent(ticketId)}/chat/stream`,
    {
      method: 'POST',
      body: formData,
      signal: options.signal,
      headers: {
        Accept: 'application/x-ndjson',
      },
    },
  );

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('AI stream unavailable.');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  const emitLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const event = JSON.parse(trimmed) as TicketCopilotStreamEvent;
    options.onEvent(event);
    if (event.type === 'error') {
      throw new Error(event.error || 'AI stream failed.');
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

    let newlineIndex = buffer.indexOf('\n');
    while (newlineIndex >= 0) {
      const line = buffer.slice(0, newlineIndex);
      buffer = buffer.slice(newlineIndex + 1);
      emitLine(line);
      newlineIndex = buffer.indexOf('\n');
    }

    if (done) break;
  }

  if (buffer.trim()) {
    emitLine(buffer);
  }
}
